import { useCallback, useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { CalendarDays, CalendarPlus, ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const POSITION_LABEL = {
  BARISTA: "Barista",
  KITCHEN: "Kitchen",
  FRONT: "Front of House",
  ALL_ROUNDER: "All-Rounder",
  OTHER: "Other",
};

function toLocalIso(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function mondayIso(input) {
  const d = input ? new Date(input + "T00:00:00") : new Date();
  const weekday = (d.getDay() + 6) % 7; // Mon = 0
  d.setDate(d.getDate() - weekday);
  d.setHours(0, 0, 0, 0);
  return toLocalIso(d);
}

function addDaysIso(iso, delta) {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + delta);
  return toLocalIso(d);
}

function fmtRange(weekStart) {
  const end = addDaysIso(weekStart, 6);
  const s = new Date(weekStart + "T00:00:00");
  const e = new Date(end + "T00:00:00");
  const m = { month: "short", day: "numeric" };
  return `${s.toLocaleDateString([], m)} – ${e.toLocaleDateString([], m)}`;
}

export function MyRosterCard() {
  const [weekStart, setWeekStart] = useState(() => mondayIso());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncedAt, setSyncedAt] = useState(null);

  const load = useCallback(async (ws) => {
    setLoading(true);
    try {
      const res = await api.get("/shifts/team-roster", { params: { week_start: ws } });
      setData(res.data);
      setSyncedAt(new Date());
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(weekStart); }, [load, weekStart]);

  // Poll every 60s so a newly-published roster shows up without a page reload.
  useEffect(() => {
    const t = setInterval(() => load(weekStart), 60000);
    return () => clearInterval(t);
  }, [load, weekStart]);

  const moveWeek = (delta) => setWeekStart(addDaysIso(weekStart, delta * 7));
  const goToday = () => setWeekStart(mondayIso());
  const isThisWeek = weekStart === mondayIso();

  const downloadICS = async () => {
    try {
      const res = await api.get("/shifts/roster-mine.ics", {
        params: { week_start: weekStart, weeks: 4 },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "text/calendar;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `coffee-lovers-shifts-${weekStart}.ics`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      toast.success("Calendar file downloaded — open it to add your shifts");
    } catch {
      toast.error("Couldn't generate calendar file. Try again in a moment.");
    }
  };

  // Build [{iso, label, dom}] for the 7 days of this week
  const days = useMemo(() => Array.from({ length: 7 }).map((_, i) => {
    const d = new Date(weekStart + "T00:00:00");
    d.setDate(d.getDate() + i);
    return { iso: toLocalIso(d), label: DAYS[i], dom: d.getDate() };
  }), [weekStart]);

  // Build a {`${userId}|${dateIso}`: [shifts...]} map
  const shiftsByCell = useMemo(() => {
    const m = {};
    (data?.shifts || []).forEach((s) => {
      const key = `${s.user_id}|${s.date}`;
      (m[key] = m[key] || []).push(s);
    });
    return m;
  }, [data]);

  const positionColor = (code) =>
    data?.positions?.find((p) => p.code === code)?.color || "#6B7280";

  const published = !!data?.published;
  const employees = data?.employees || [];
  const me = data?.me;

  return (
    <Card className="border-gray-200" data-testid="my-roster-card">
      <CardContent className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-3 gap-3 flex-wrap">
          <div className="min-w-0">
            <h3 className="font-heading text-lg font-bold text-[#1A2744] flex items-center gap-2">
              <CalendarDays className="h-4 w-4 text-[#C9A84C]" strokeWidth={1.8} />
              Team roster
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {fmtRange(weekStart)}{isThisWeek ? " · this week" : ""}
            </p>
          </div>
          <div className="flex items-center gap-1.5">
            <Badge
              variant="outline"
              className={
                published
                  ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                  : "bg-amber-50 text-amber-800 border-amber-200"
              }
              data-testid="my-roster-status"
            >
              {published ? "PUBLISHED" : "NOT PUBLISHED"}
            </Badge>
            <Button
              variant="outline"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={() => load(weekStart)}
              disabled={loading}
              data-testid="my-roster-refresh"
              aria-label="Refresh roster"
              title={syncedAt ? `Last checked ${syncedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false })}` : "Refresh"}
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
            </Button>
            {published && (
              <Button
                variant="outline"
                size="sm"
                className="h-7 px-2 text-[11px] gap-1 border-[#C9A84C]/40 text-[#1A2744] hover:bg-[#FFF8E1]"
                onClick={downloadICS}
                data-testid="my-roster-ics-export"
                aria-label="Add my shifts to calendar"
                title="Download .ics — opens in Apple Calendar, Google Calendar, Outlook"
              >
                <CalendarPlus className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Add to calendar</span>
              </Button>
            )}
          </div>
        </div>

        {/* Week navigator */}
        <div className="flex items-center gap-1.5 mb-4">
          <Button
            variant="outline"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => moveWeek(-1)}
            data-testid="my-roster-prev"
            aria-label="Previous week"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant={isThisWeek ? "default" : "outline"}
            size="sm"
            className={`h-8 text-xs ${isThisWeek ? "bg-[#1A2744] text-white" : ""}`}
            onClick={goToday}
            data-testid="my-roster-today"
          >
            This week
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => moveWeek(1)}
            data-testid="my-roster-next"
            aria-label="Next week"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {/* Legend (only when there's something to colour) */}
        {published && data?.positions?.length > 0 && (
          <div className="flex flex-wrap gap-3 text-[11px] mb-4" data-testid="my-roster-legend">
            {data.positions.map((p) => (
              <span key={p.code} className="inline-flex items-center gap-1.5 text-gray-600">
                <span className="inline-block w-3 h-3 rounded" style={{ backgroundColor: p.color }}></span>
                {POSITION_LABEL[p.code] || p.code}
              </span>
            ))}
          </div>
        )}

        {/* Body */}
        {loading && !data ? (
          <p className="text-sm text-gray-500" data-testid="my-roster-loading">Loading…</p>
        ) : !published ? (
          <p className="text-sm text-gray-500" data-testid="my-roster-not-published">
            Your manager hasn't published this week's roster yet. Check back soon.
          </p>
        ) : employees.length === 0 ? (
          <p className="text-sm text-gray-500" data-testid="my-roster-empty">
            No shifts on this week's published roster yet.
          </p>
        ) : (
          <>
            {/* Desktop / landscape grid view ----------------------------- */}
            <div
              className="hidden md:block landscape:block overflow-x-auto"
              data-testid="my-roster-grid"
            >
              <div className="min-w-[820px]">
                {/* Header row */}
                <div className="grid grid-cols-[160px_repeat(7,minmax(0,1fr))] border-b border-gray-200 bg-gray-50 rounded-t-md">
                  <div className="p-2.5 text-[10px] uppercase tracking-widest text-gray-500 font-semibold">
                    Employee
                  </div>
                  {days.map((d) => (
                    <div key={d.iso} className="p-2.5 border-l border-gray-200 text-xs">
                      <div className="font-semibold text-[#1A2744]">{d.label}</div>
                      <div className="text-gray-500">{d.dom}</div>
                    </div>
                  ))}
                </div>

                {/* Employee rows */}
                {employees.map((emp) => {
                  const isMe = emp.id === me;
                  return (
                    <div
                      key={emp.id}
                      className={`grid grid-cols-[160px_repeat(7,minmax(0,1fr))] border-b border-gray-100 ${
                        isMe ? "bg-[#FFF8E1]" : "hover:bg-[#FAFAF7]"
                      }`}
                      data-testid={`team-row-${emp.id}`}
                    >
                      <div className="p-2.5 min-w-0">
                        <div className={`text-sm font-medium truncate ${isMe ? "text-[#1A2744]" : "text-[#1A2744]"}`}>
                          {emp.name}{isMe && <span className="text-[10px] text-[#C9A84C] ml-1 font-semibold">YOU</span>}
                        </div>
                        <div className="text-[10px] text-gray-500">{emp.week_hours}h scheduled</div>
                      </div>
                      {days.map((d) => {
                        const cell = shiftsByCell[`${emp.id}|${d.iso}`] || [];
                        return (
                          <div
                            key={d.iso}
                            className="border-l border-gray-100 p-1.5 min-h-[60px]"
                          >
                            {cell.map((s) => (
                              <div
                                key={s.id}
                                className="w-full mb-1 rounded-md px-2 py-1.5 text-[11px] text-white font-medium"
                                style={{ backgroundColor: positionColor(s.position) }}
                                data-testid={`team-shift-${s.id}`}
                              >
                                <div className="font-semibold">{s.start_time}–{s.end_time}</div>
                                <div className="opacity-90 text-[10px] truncate">
                                  {POSITION_LABEL[s.position] || s.position}
                                </div>
                              </div>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Mobile portrait: day-by-day cards -------------------------- */}
            <div
              className="md:hidden landscape:hidden space-y-3"
              data-testid="my-roster-mobile"
            >
              {days.map((d) => {
                const todays = (data?.shifts || []).filter((s) => s.date === d.iso);
                if (todays.length === 0) {
                  return (
                    <div
                      key={d.iso}
                      className="rounded-lg border border-dashed border-gray-200 p-3"
                      data-testid={`mobile-day-${d.iso}`}
                    >
                      <div className="flex items-baseline justify-between">
                        <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold">
                          {d.label} {d.dom}
                        </div>
                        <div className="text-[11px] text-gray-400">No one rostered</div>
                      </div>
                    </div>
                  );
                }
                // Sort: me first, then by start_time
                const sorted = [...todays].sort((a, b) => {
                  if (a.user_id === me && b.user_id !== me) return -1;
                  if (b.user_id === me && a.user_id !== me) return 1;
                  return a.start_time.localeCompare(b.start_time);
                });
                return (
                  <div
                    key={d.iso}
                    className="rounded-lg border border-gray-200 overflow-hidden"
                    data-testid={`mobile-day-${d.iso}`}
                  >
                    <div className="bg-gray-50 px-3 py-2 flex items-baseline justify-between border-b border-gray-200">
                      <div className="text-xs uppercase tracking-widest text-[#1A2744] font-semibold">
                        {d.label} {d.dom}
                      </div>
                      <div className="text-[11px] text-gray-500">
                        {todays.length} {todays.length === 1 ? "shift" : "shifts"}
                      </div>
                    </div>
                    <ul className="divide-y divide-gray-100">
                      {sorted.map((s) => {
                        const emp = employees.find((e) => e.id === s.user_id);
                        const isMe = s.user_id === me;
                        return (
                          <li
                            key={s.id}
                            className={`flex items-center gap-3 px-3 py-2.5 ${isMe ? "bg-[#FFF8E1]" : ""}`}
                            data-testid={`team-shift-${s.id}`}
                          >
                            <span
                              className="inline-block w-1 h-10 rounded-full shrink-0"
                              style={{ backgroundColor: positionColor(s.position) }}
                            ></span>
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium text-[#1A2744] truncate">
                                {emp?.name || "—"}
                                {isMe && <span className="text-[10px] text-[#C9A84C] ml-1 font-semibold">YOU</span>}
                              </div>
                              <div className="text-[11px] text-gray-500">
                                {POSITION_LABEL[s.position] || s.position}
                              </div>
                            </div>
                            <div className="font-mono text-sm font-semibold text-[#1A2744] shrink-0">
                              {s.start_time}–{s.end_time}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
